import type { OAuthErrorContext } from "./auth.types";

type AuthClientError = {
  message?: string | undefined;
};

type AuthClientResult = {
  data?: unknown;
  error?: AuthClientError | null;
};

function getAuthClientErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export async function settleAuthClientResult<Result extends AuthClientResult>(
  action: () => Promise<Result>,
  fallback: string,
) {
  try {
    const result = await action();

    if (result?.error) {
      return {
        errorMessage: result.error.message || fallback,
        result,
      };
    }

    return {
      errorMessage: null,
      result,
    };
  } catch (error) {
    return {
      errorMessage: getAuthClientErrorMessage(error, fallback),
      result: null,
    };
  }
}

export async function settleAsyncResult<T>(action: () => Promise<T>, fallback: string) {
  try {
    return {
      data: await action(),
      errorMessage: null,
    };
  } catch (error) {
    return {
      data: null,
      errorMessage: getAuthClientErrorMessage(error, fallback),
    };
  }
}

export function getOAuthErrorMessage(
  context: OAuthErrorContext | unknown,
  fallback: string,
) {
  if (
    context &&
    typeof context === "object" &&
    "error" in context &&
    context.error &&
    typeof context.error === "object" &&
    "message" in context.error &&
    typeof context.error.message === "string" &&
    context.error.message.length > 0
  ) {
    return context.error.message;
  }

  return fallback;
}
